import { useState, useEffect, useMemo } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  parseISO,
} from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useOffline } from '../../context/OfflineContext';
import { useToast } from '../../context/ToastContext';
import { getEvents } from '../../services/syncService';
import { tripsApi } from '../../services/api';
import { getTimeInTimezone } from '../../utils/timezone';
import { ErrorBoundary } from '../UI/ErrorBoundary';
import EntryModal from '../Entry/EntryModal';
import DayEventsModal from './DayEventsModal';
import CalendarHeader from './CalendarHeader';
import TimezoneSelector from './TimezoneSelector';
import TripSelector from './TripSelector';
import CalendarMonthGrid from './CalendarMonthGrid';
import CalendarWeekView from './CalendarWeekView';
import CalendarLegend from './CalendarLegend';
import CalendarSkeleton from './CalendarSkeleton';

/**
 * Calendar - Main calendar view component
 * Displays events in month (desktop) or week (mobile) view
 */
export default function Calendar() {
  const { user } = useAuth();
  const { isOnline, isReady } = useOffline();
  const toast = useToast();
  
  // View state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [isMobile, setIsMobile] = useState(false);
  
  // Data state
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Trips state
  const [trips, setTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [tripsLoading, setTripsLoading] = useState(true);
  
  // Modal state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEntryModalOpen, setIsEntryModalOpen] = useState(false);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [dayModalEvents, setDayModalEvents] = useState([]);
  
  // Timezone state
  const [selectedTimezone, setSelectedTimezone] = useState('home');
  const homeTimezone = user?.home_timezone || 'Europe/Dublin';
  
  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load trips on mount
  useEffect(() => {
    if (isOnline) {
      loadTrips();
    } else {
      setTripsLoading(false);
    }
  }, [isOnline]);

  async function loadTrips() {
    try {
      setTripsLoading(true);
      const data = await tripsApi.list();
      setTrips(data);
    } catch (err) {
      console.error('Failed to load trips:', err);
    } finally {
      setTripsLoading(false);
    }
  }

  async function handleCreateTrip(name) {
    try {
      const newTrip = await tripsApi.create({ name });
      setTrips(prev => [newTrip, ...prev]);
      toast.success('Trip created');
    } catch (err) {
      toast.error('Failed to create trip');
    }
  }

  // Load events when ready or when month changes
  useEffect(() => {
    if (isReady) {
      loadEvents();
    }
  }, [currentMonth, isOnline, isReady]);

  async function loadEvents() {
    try {
      setLoading(true);
      const start = startOfMonth(subMonths(currentMonth, 1));
      const end = endOfMonth(addMonths(currentMonth, 1));
      
      const data = await getEvents(isOnline, {
        start: start.toISOString(),
        end: end.toISOString(),
      });
      setEvents(data);
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setLoading(false);
    }
  }

  // Extract unique timezones from events in current view
  const availableTimezones = useMemo(() => {
    const tzSet = new Set();
    
    const viewStart = isMobile 
      ? startOfWeek(currentWeek, { weekStartsOn: 1 })
      : startOfMonth(currentMonth);
    const viewEnd = isMobile 
      ? endOfWeek(currentWeek, { weekStartsOn: 1 })
      : endOfMonth(currentMonth);
    
    events.forEach(event => {
      const eventStart = parseISO(event.start_datetime);
      const eventEnd = parseISO(event.end_datetime);
      const overlapsView = eventStart <= viewEnd && eventEnd >= viewStart;
      
      if (overlapsView) {
        if (event.local_timezone && event.local_timezone !== homeTimezone) {
          tzSet.add(event.local_timezone);
        }
        if (event.arrival_timezone && event.arrival_timezone !== homeTimezone) {
          tzSet.add(event.arrival_timezone);
        }
      }
    });
    return Array.from(tzSet).sort();
  }, [events, homeTimezone, isMobile, currentWeek, currentMonth]);

  // Calendar days for month view
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [currentMonth]);

  // Week days for mobile view
  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [currentWeek]);

  // Group events by day (filtered by selected trip)
  const eventsByDay = useMemo(() => {
    const grouped = {};
    
    // Filter events by selected trip
    const filteredEvents = selectedTripId 
      ? events.filter(e => e.trip_id === selectedTripId)
      : events;
    
    filteredEvents.forEach(event => {
      const start = parseISO(event.start_datetime);
      const end = parseISO(event.end_datetime);
      
      let current = new Date(start);
      current.setHours(0, 0, 0, 0);
      
      const endDay = new Date(end);
      endDay.setHours(0, 0, 0, 0);
      
      while (current <= endDay) {
        const key = format(current, 'yyyy-MM-dd');
        if (!grouped[key]) grouped[key] = [];
        
        grouped[key].push({
          ...event,
          isStart: isSameDay(current, start),
          isEnd: isSameDay(current, end),
          isMiddle: !isSameDay(current, start) && !isSameDay(current, end),
        });
        
        current = addDays(current, 1);
      }
    });
    
    return grouped;
  }, [events, selectedTripId]);

  // Navigation handlers
  function handlePrevMonth() {
    setCurrentMonth(prev => subMonths(prev, 1));
  }

  function handleNextMonth() {
    setCurrentMonth(prev => addMonths(prev, 1));
  }

  function handlePrevWeek() {
    setCurrentWeek(prev => subWeeks(prev, 1));
    const newWeek = subWeeks(currentWeek, 1);
    if (!isSameMonth(newWeek, currentMonth)) {
      setCurrentMonth(newWeek);
    }
  }

  function handleNextWeek() {
    setCurrentWeek(prev => addWeeks(prev, 1));
    const newWeek = addWeeks(currentWeek, 1);
    if (!isSameMonth(newWeek, currentMonth)) {
      setCurrentMonth(newWeek);
    }
  }

  function handleToday() {
    const today = new Date();
    setCurrentMonth(today);
    setCurrentWeek(today);
  }

  // Event handlers
  function handleDayClick(day, dayEvents) {
    if (dayEvents.length > 0) {
      setSelectedDate(day);
      setDayModalEvents(dayEvents);
      setIsDayModalOpen(true);
    } else {
      setSelectedDate(day);
      setSelectedEvent(null);
      setIsEntryModalOpen(true);
    }
  }

  function handleEventClick(event) {
    setSelectedEvent(event);
    setSelectedDate(null);
    setIsEntryModalOpen(true);
  }

  function handleAddNewFromDay() {
    setSelectedEvent(null);
    setIsEntryModalOpen(true);
  }

  function handleAddEntry() {
    setSelectedDate(new Date());
    setSelectedEvent(null);
    setIsEntryModalOpen(true);
  }

  function handleEntryModalClose() {
    setIsEntryModalOpen(false);
    setSelectedEvent(null);
  }

  function handleDayModalClose() {
    setIsDayModalOpen(false);
    setDayModalEvents([]);
  }

  // Get display time for event based on selected timezone
  function getEventDisplayTime(event) {
    if (event.isMiddle) return null;
    
    let datetime, eventLocalTimezone;
    const isFlightOrTransit = event.category === 'flight' || event.category === 'transit';
    
    if (event.isStart) {
      datetime = event.start_datetime;
      eventLocalTimezone = event.local_timezone;
    } else if (event.isEnd) {
      datetime = event.end_datetime;
      eventLocalTimezone = event.arrival_timezone || event.local_timezone;
    }

    const displayTimezone = selectedTimezone === 'home' ? homeTimezone : selectedTimezone;
    const displayTime = getTimeInTimezone(datetime, displayTimezone);

    if (event.isStart && event.isEnd) {
      return displayTime;
    } else if (event.isStart) {
      return isFlightOrTransit ? `Dep ${displayTime}` : displayTime;
    } else if (event.isEnd) {
      return isFlightOrTransit ? `Arr ${displayTime}` : displayTime;
    }
    
    return null;
  }

  // Show skeleton while loading initial data
  if (loading && events.length === 0) {
    return <CalendarSkeleton />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <CalendarHeader
          currentMonth={currentMonth}
          currentWeek={currentWeek}
          isMobile={isMobile}
          eventsCount={events.length}
          onPrevious={isMobile ? handlePrevWeek : handlePrevMonth}
          onNext={isMobile ? handleNextWeek : handleNextMonth}
          onToday={handleToday}
          onAddEntry={handleAddEntry}
        />

        <div className="flex flex-wrap items-center gap-3">
          {isOnline && (
            <TripSelector
              trips={trips}
              selectedTripId={selectedTripId}
              onSelectTrip={setSelectedTripId}
              onCreateTrip={handleCreateTrip}
              loading={tripsLoading}
            />
          )}
          <TimezoneSelector
            selectedTimezone={selectedTimezone}
            onChange={setSelectedTimezone}
            availableTimezones={availableTimezones}
          />
        </div>
      </div>

      {/* Desktop Month View */}
      <div className="hidden md:block">
        <CalendarMonthGrid
          calendarDays={calendarDays}
          currentMonth={currentMonth}
          eventsByDay={eventsByDay}
          onDayClick={handleDayClick}
          getEventDisplayTime={getEventDisplayTime}
        />
      </div>

      {/* Mobile Week View */}
      <div className="md:hidden">
        <CalendarWeekView
          weekDays={weekDays}
          eventsByDay={eventsByDay}
          onDayClick={handleDayClick}
          getEventDisplayTime={getEventDisplayTime}
        />
      </div>

      {/* Legend */}
      <CalendarLegend />

      {/* Day Events Modal */}
      <DayEventsModal
        isOpen={isDayModalOpen}
        onClose={handleDayModalClose}
        date={selectedDate}
        events={dayModalEvents}
        onEventClick={handleEventClick}
        onAddNew={handleAddNewFromDay}
        homeTimezone={homeTimezone}
        selectedTimezone={selectedTimezone}
      />

      {/* Entry Modal with error boundary */}
      <ErrorBoundary
        fallbackRender={({ resetError }) => (
          <ModalErrorFallback 
            onClose={() => {
              resetError();
              handleEntryModalClose();
            }}
            title="Failed to load entry form"
          />
        )}
      >
        <EntryModal
          isOpen={isEntryModalOpen}
          onClose={handleEntryModalClose}
          onSave={() => {
            loadEvents();
            loadTrips(); // Refresh trip event counts
            handleEntryModalClose();
          }}
          event={selectedEvent}
          defaultDate={selectedDate}
          trips={trips}
          defaultTripId={selectedTripId}
        />
      </ErrorBoundary>
    </div>
  );
}

/**
 * Error fallback for modals - shows error with close button
 */
function ModalErrorFallback({ onClose, title }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div 
        className="rounded-lg p-6 max-w-sm w-full text-center"
        style={{ backgroundColor: 'var(--color-bg-canvas)' }}
      >
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: 'rgba(193, 96, 92, 0.1)' }}
        >
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="var(--color-status-error)"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <h3 
          className="text-lg font-medium mb-2"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {title}
        </h3>
        <p 
          className="text-sm mb-4"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          An error occurred. Please try again.
        </p>
        <button onClick={onClose} className="btn-primary">
          Close
        </button>
      </div>
    </div>
  );
}
